/**
 * API/backend error message mapping and error page copy.
 * Maps raw API error strings to error codes and user-facing messages for the Error page.
 */

export const errorMessages = Object.freeze({
  invalidCreditCard: {
    response: 'The credit-card number passed is not valid',
    message: 'Die angegebene Kreditkartennummer ist nicht gültig. Bitte überprüfen Sie die Kartennummer und versuchen Sie es erneut.'
  },
  preAuthFailed: {
    response: 'The credit card is not correct. A pre authorisation failed',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Kreditkarte wurde vom Kartenaussteller abgelehnt. Bitte verwenden Sie eine andere Karte oder wenden Sie sich an Ihre Bank.',
    redirect: true,
    showTryAgain: false
  },
  invalidCreditCardDate: {
    response: 'Credit Card Expiry Date Invalid',
    message: 'Das Ablaufdatum der Kreditkarte ist ungültig. Bitte prüfen Sie Monat und Jahr und versuchen Sie es erneut.'
  },
  noCancelDirectDebit: {
    response: 'Failure to cancel existing direct debit wallet',
    message: 'Ihr bestehender SEPA Auftrag konnte nicht geändert werden. Bitte kontaktieren Sie das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.'
  },
  sepaSessionExpired: {
    response: 'Session has expired.',
    redirect: true
  },
  forbidden: {
    response: 'Forbidden',
    message: 'Der Zugriff wurde verweigert. Bitte versuchen Sie es etwas später noch einmal.'
  },
  invalidEmailOrPassword: {
    response: 'Kombination E-Mail-Adresse und Passwort ist ungültig.',
    message: 'Die Kombination E-Mail-Adresse und Passwort ist ungültig.'
  },
  userExists: {
    response: 'User exists',
    message: `Die E-Mail-Adresse <strong>{{userEmail}}</strong> ist bereits registriert. <a href="${
      import.meta.env.VITE_APP_PASSWORD_FORGOTTEN_URL
    }">Passwort vergessen</a>?`
  },
  incorrectEmailOrPassword: {
    response: 'Incorrect username or password.',
    message: 'Ungültiger Benutzername oder Passwort'
  },
  invalidCvv: {
    response: 'CV2 must be 3 digits in length.',
    message:
      'Die Prüfziffer (CVC/CVV) ist ungültig. Sie muss aus 3 Ziffern bestehen. Bitte überprüfen Sie die Prüfziffer auf der Karte und versuchen Sie es erneut.'
  },
  paymentNotAuthorized: {
    response: 'Payment could not be Authorized.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Zahlung wurde von Ihrer Bank nicht autorisiert. Bitte verwenden Sie eine andere Zahlungsmethode oder kontaktieren Sie Ihre Bank.',
    redirect: true,
    showTryAgain: false
  },
  cardNotAuthorised: {
    response: 'The customers Credit or Debit card has not been authorised',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Kredit- oder Debitkarte wurde nicht autorisiert. Bitte verwenden Sie eine andere Karte oder wenden Sie sich an Ihre Bank.',
    redirect: true,
    showTryAgain: false
  },
  incomingttokenExpired: {
    response: 'The incoming token has expired.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Sitzung ist abgelaufen.',
    redirect: true,
    showTryAgain: false
  },
  invalid3dsData: {
    response: 'Invalid data provided to proceed with 3D Secure Authentication.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Zahlung konnte mit 3D Secure nicht autorisiert werden. Bitte wenden Sie sich an Ihre Bank.',
    redirect: true,
    showTryAgain: false
  },
  endpointTimeout: {
    response: 'Endpoint request timed out',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Paypal-Sitzung ist abgelaufen oder es ist ein Verbindungsfehler aufgetreten. Bitte versuchen Sie die Zahlung erneut.',
    redirect: true
  },
  noEwallet: {
    response: 'Account has no eWallet.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Die Paypal-Verknüpfung konnte nicht geladen werden. Bitte prüfen Sie Ihr Paypal-Konto oder wählen Sie eine andere Zahlungsmethode.',
    redirect: true
  },
  undeterminedServicePrice: {
    response: "Service Price couldn't be determined",
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message:
      'Aufgrund eines technischen Fehlers konnte der Preis nicht ermittelt werden (Fehlercode CP100). Bitte versuchen Sie es später erneut oder wenden Sie sich an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.',
    redirect: true
  },
  invalid3dsProcess: {
    response: 'Unable to validate the 3D Secure Process due to downstream partners.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message:
      'Bei der Autorisierung über 3D Secure ist ein technischer Fehler aufgetreten (Fehlercode CP350). Bitte wenden Sie sich an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.',
    redirect: true,
    showTryAgain: false
  },
  existingSubscription: {
    response: 'Cannot add another subscription to contractReference:',
    redirect: true
  },
  existingService: {
    response: 'A subscription for this service already exists',
    redirect: true
  },
  emailInUse: {
    response: 'The email is already in use.',
    redirect: true
  },
  invalid3dsToken: {
    response: '3D secure token invalid',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message:
      'Die 3D‑Secure-Autorisierung konnte nicht abgeschlossen werden (Fehlercode CP300). Bitte wenden Sie sich an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.',
    redirect: true,
    showTryAgain: false
  },
  invalidParameters: {
    response: 'One Or More Invalid Parameters',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message:
      'Die Bestellung konnte aufgrund eines technischen Fehlers nicht abgeschlossen werden (Fehlercode CP110). Bitte versuchen Sie es später erneut oder wenden Sie sich an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.',
    redirect: true
  },
  preconditionFailed: {
    response: 'Precondition Failed.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message:
      'Die Bestellung konnte aufgrund eines technischen Fehlers nicht abgeschlossen werden (Fehlercode CP120). Bitte versuchen Sie es später erneut oder wenden Sie sich an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.',
    redirect: true
  },
  authorizationDenied: {
    response: 'Authorisation denied: the supplied session ID does not have the correct privilege for this request',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Ihre Sitzung ist abgelaufen.',
    redirect: true
  },
  accountAlreadyAddingSubscription: {
    response: 'This account is already currently adding a subscription, please try again later.',
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    message: 'Für dieses Konto wird gerade eine Bestellung durchgeführt. Bitte versuchen Sie es in Kürze erneut.',
    redirect: true
  },
  accountNotFound: {
    response: 'Account cannot be found. Invalid UKI and PIN or Password.',
    redirect: true,
    showTryAgain: false
  },
  accountNotActive: {
    response: 'PreAuthentication failed with error Your account is not active anymore. Please reset your password to reactivate.',
    title: 'Bei der Anmeldung ist ein Fehler aufgetreten',
    message: `Ihr Konto ist inaktiv. Bitte setzen Sie Ihr Passwort zurück, um es zu reaktivieren. <a href="${import.meta.env.VITE_APP_PASSWORD_FORGOTTEN_URL}">Passwort zurücksetzen</a>`,
    redirect: true,
    showTryAgain: false
  },

  // Generic Error Message
  default: {
    message: 'Es ist ein unerwarteter Fehler aufgetreten. Bitte versuchen sie es etwas später noch einmal.',
    redirect: true
  }
} as const);

export const errorPageMessages = Object.freeze({
  pageNotFound: {
    title: 'Es ist ein Fehler aufgetreten',
    body: `
            <p>Das angeforderte Produkt ist uns nicht bekannt.</p>
            <p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at" title="DerStandard.at ABO">abo.derstandard.at</a></p>
        `,
    showTryAgain: false
  },
  sessionExpiredUnknownProduct: {
    title: 'Die Sitzung ist abgelaufen',
    body: `
            <p>Das angeforderte Produkt ist uns nicht bekannt.</p>
            <p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at" title="DerStandard.at ABO">abo.derstandard.at</a></p>
        `
  },
  paypalCancelled: {
    title: 'Die Zahlung wurde durch Paypal abgebrochen',
    body: `
            <p>Falls dieser Abbruch unerwünscht war, versuchen Sie es bitte zu einem späteren Zeitpunkt erneut.</p>
        `
  },
  activeProduct: {
    title: 'Sie haben bereits dieses Abo oder ein Abo aus dieser Produktgruppe erworben.',
    body: '<p>Falls hier ein Fehler vorliegt, wenden Sie sich bitte an das <a href="mailto:aboservice@derstandard.at">Aboservice</a>.</p>',
    showTryAgain: false
  },
  existingSubscription: {
    title: 'Bei der Bestellung ist ein Fehler aufgetreten',
    body: '<p>Für dieses Konto besteht bereits ein aktives Abo.</p>',
    showTryAgain: false
  },
  emailInUse: {
    title: 'Bei der Anmeldung ist ein Fehler aufgetreten',
    body: '<p>Ihre Anmeldung kann mit diesem Konto aktuell nicht durchgeführt werden. Bitte wenden Sie sich an <a href="mailto:digitalabo@derstandard.at">digitalabo@derstandard.at</a>.</p>',
    showTryAgain: false
  },
  accountNotFound: {
    title: 'Bei der Anmeldung ist ein Fehler aufgetreten',
    body: '<p>Ihre Anmeldung kann mit diesem Konto aktuell nicht durchgeführt werden. Bitte wenden Sie sich an <a href="mailto:digitalabo@derstandard.at">digitalabo@derstandard.at</a>.</p>',
    showTryAgain: false
  },
  default: {
    title: 'Es ist ein Fehler aufgetreten',
    body: '<p>Alle aktuellen Angebote finden Sie unter <a href="https://abo.derstandard.at">abo.derstandard.at</a></p>',
    showTryAgain: false
  }
} as const);

const errorMessageEntriesByResponseLength = (Object.entries(errorMessages) as Array<[string, (typeof errorMessages)[keyof typeof errorMessages]]>)
  .filter(([, value]) => 'response' in value && typeof (value as { response: string }).response === 'string')
  .map(([key, value]) => [key, (value as { response: string }).response] as const)
  .sort(([, a], [, b]) => b.length - a.length);

/**
 * Maps an API/backend error message to an error code key from errorMessages.
 * Uses exact match or startsWith so backend variations (e.g. extra text) are covered.
 * Prefers longer (more specific) response strings when multiple could match.
 * @param message - Raw error message from API/backend
 * @returns The error code key (e.g. 'preAuthFailed') or null if no match
 */
export function getErrorCodeFromMessage(message: string | undefined): string | null {
  if (!message || typeof message !== 'string') return null;

  for (const [key, response] of errorMessageEntriesByResponseLength) {
    if (message === response || message.startsWith(response)) return key;
  }
  return null;
}

/**
 * Returns display title, message and whether to redirect to the error page for a given error code.
 * Used to decide: show in-page Alert (popup) when redirect is false, or navigate to error page when true.
 * @param code - Error code key from errorMessages
 * @returns Object with title, message (may contain HTML) and redirect flag
 */
export function getErrorDisplay(code: string): { title: string; message: string; redirect: boolean } {
  const entry = errorMessages[code as keyof typeof errorMessages];
  const defaultTitle = errorPageMessages.default.title;
  const defaultBody = errorPageMessages.default.body.replace(/<[^>]+>/g, '').trim();

  if (!entry) {
    return { title: defaultTitle, message: defaultBody, redirect: true };
  }

  const title = 'title' in entry && entry.title ? entry.title : defaultTitle;
  const message = 'message' in entry && entry.message ? entry.message : defaultBody;
  const redirect = 'redirect' in entry && entry.redirect === true;

  return { title, message, redirect };
}
